import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, Users, Pencil, Trash2, Eye, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import EmployeeForm from "../components/EmployeeForm";
import { useAuth } from "../context/AuthContext";
import { employeesApi } from "../lib/api";
import { DEPARTMENTS } from "../mock";
import { useToast } from "../hooks/use-toast";

function Avatar({ emp }) {
  const initials = `${emp.first_name?.[0] || ""}${emp.last_name?.[0] || ""}`;
  return emp.avatar ? (
    <img src={emp.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
  ) : (
    <div className="w-10 h-10 rounded-full bg-brand-light text-brand-blue flex items-center justify-center text-sm font-semibold">{initials}</div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-slate-50 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-brand-navy text-right">{value || "—"}</span>
    </div>
  );
}

function EmployeeDetail({ emp, open, onOpenChange }) {
  if (!emp) return null;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="mb-4">
          <SheetTitle className="sr-only">Employee details</SheetTitle>
        </SheetHeader>
        <div className="flex items-center gap-4 mb-6">
          <Avatar emp={emp} />
          <div>
            <div className="font-display font-bold text-lg text-brand-navy">{emp.first_name} {emp.last_name}</div>
            <div className="text-sm text-slate-500">{emp.job_title} · {emp.department}</div>
          </div>
          <Badge className={`ml-auto ${emp.status === "Active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"} hover:bg-current/10`}>{emp.status}</Badge>
        </div>
        <div className="space-y-6">
          <div><h4 className="font-semibold text-brand-navy mb-1 text-sm">Identity</h4>
            <DetailRow label="Employee no." value={emp.employee_number} />
            <DetailRow label="Sex" value={emp.sex} />
            <DetailRow label="Date of birth" value={emp.date_of_birth} />
            <DetailRow label="Marital status" value={emp.marital_status} />
            <DetailRow label="National ID" value={emp.national_id} />
            <DetailRow label="Address" value={emp.address} />
            <DetailRow label="Phone" value={emp.phone} />
            <DetailRow label="Email" value={emp.email} />
          </div>
          <div><h4 className="font-semibold text-brand-navy mb-1 text-sm">Next of kin</h4>
            <DetailRow label="Name" value={emp.next_of_kin_name} />
            <DetailRow label="Relationship" value={emp.next_of_kin_relationship} />
            <DetailRow label="Address" value={emp.next_of_kin_address} />
          </div>
          <div><h4 className="font-semibold text-brand-navy mb-1 text-sm">Employment</h4>
            <DetailRow label="Nature" value={emp.nature_of_employment} />
            <DetailRow label="Type" value={emp.employment_type} />
            <DetailRow label="Commenced" value={emp.commencement_date} />
            <DetailRow label="Probation ends" value={emp.probation_end_date} />
            <DetailRow label="Notice period" value={emp.notice_period} />
          </div>
          <div><h4 className="font-semibold text-brand-navy mb-1 text-sm">Wages</h4>
            <DetailRow label="Wage rate" value={`P${Number(emp.wage_rate).toLocaleString()} / ${emp.pay_interval}`} />
            <DetailRow label="Components" value={emp.wage_components} />
            <DetailRow label="Night-work" value={emp.night_work ? "Yes (5% allowance)" : "No"} />
          </div>
          <div><h4 className="font-semibold text-brand-navy mb-1 text-sm">Leave, hours & rest</h4>
            <DetailRow label="Annual leave" value={`${emp.annual_leave_entitlement} days`} />
            <DetailRow label="Sick leave" value={`${emp.sick_leave_entitlement} days`} />
            <DetailRow label="Public holidays" value={`${emp.public_holidays_entitlement} days`} />
            <DetailRow label="Weekly rest day" value={emp.weekly_rest_day} />
            <DetailRow label="Hours" value={`${emp.daily_hours}h/day · ${emp.weekly_hours}h/week`} />
          </div>
          {emp.status === "Terminated" && (
            <div><h4 className="font-semibold text-brand-navy mb-1 text-sm">Termination</h4>
              <DetailRow label="Date" value={emp.termination_date} />
              <DetailRow label="Reason" value={emp.termination_reason} />
              <DetailRow label="Terminal payments" value={emp.terminal_payments} />
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function Employees() {
  const { session } = useAuth();
  const orgId = session?.organization?.id;
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const [employees, setEmployees] = useState([]);
  const [query, setQuery] = useState("");
  const [dept, setDept] = useState("All");
  const [status, setStatus] = useState("All");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try { setEmployees(await employeesApi.list()); } catch (e) { /* handled by auth */ }
    setLoading(false);
  };
  useEffect(() => { if (orgId) refresh(); }, [orgId]);
  useEffect(() => { if (params.get("new")) { setEditing(null); setFormOpen(true); setParams({}); } }, [params]);

  const filtered = useMemo(() => employees.filter((e) => {
    const q = query.toLowerCase();
    const matchQ = !q || `${e.first_name} ${e.last_name} ${e.employee_number} ${e.job_title} ${e.email}`.toLowerCase().includes(q);
    const matchD = dept === "All" || e.department === dept;
    const matchS = status === "All" || e.status === status;
    return matchQ && matchD && matchS;
  }), [employees, query, dept, status]);

  const handleSave = async (data) => {
    try {
      if (data.id) await employeesApi.update(data.id, data);
      else await employeesApi.create(data);
      await refresh();
      setFormOpen(false);
      toast({ title: data.id ? "Employee updated" : "Employee added", description: `${data.first_name} ${data.last_name} saved successfully.` });
    } catch (e) {
      toast({ title: "Could not save", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    }
  };
  const confirmDelete = async () => {
    try {
      await employeesApi.remove(toDelete.id);
      await refresh();
      toast({ title: "Employee removed", description: `${toDelete.first_name} ${toDelete.last_name} was removed.` });
    } catch (e) {
      toast({ title: "Could not remove", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    }
    setToDelete(null);
  };

  return (
    <div className="pt-2 pb-6 animate-fade-up">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-brand-navy">Employees</h1>
          <p className="text-slate-500 mt-1">{employees.length} people · statutory records aligned to the Act</p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-brand-blue hover:bg-brand-bluedark text-white rounded-lg h-11 px-5"><Plus className="w-4 h-4 mr-1" /> Add Employee</Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-5 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 bg-brand-mist rounded-lg px-3 h-10 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, number, role..." className="bg-transparent outline-none text-sm w-full" />
        </div>
        <Select value={dept} onValueChange={setDept}>
          <SelectTrigger className="w-40 h-10"><SelectValue placeholder="Department" /></SelectTrigger>
          <SelectContent><SelectItem value="All">All departments</SelectItem>{DEPARTMENTS.map((d) => (<SelectItem key={d} value={d}>{d}</SelectItem>))}</SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-36 h-10"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="All">All status</SelectItem><SelectItem value="Active">Active</SelectItem><SelectItem value="Terminated">Terminated</SelectItem></SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-20 text-center">
            <div className="w-14 h-14 rounded-2xl bg-brand-light text-brand-blue flex items-center justify-center mx-auto mb-4"><Users className="w-7 h-7" /></div>
            <h3 className="font-display font-bold text-brand-navy">No employees found</h3>
            <p className="text-slate-500 text-sm mt-1">Try adjusting filters or add your first employee.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b border-slate-100 bg-slate-50/50">
                <th className="py-3.5 px-5 font-medium">Employee</th>
                <th className="py-3.5 px-3 font-medium hidden md:table-cell">Department</th>
                <th className="py-3.5 px-3 font-medium hidden lg:table-cell">Employment</th>
                <th className="py-3.5 px-3 font-medium hidden lg:table-cell">Wage</th>
                <th className="py-3.5 px-3 font-medium">Status</th>
                <th className="py-3.5 px-5 font-medium text-right">Actions</th>
              </tr></thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.id} className="border-b border-slate-50 last:border-0 hover:bg-brand-mist/40 transition-colors">
                    <td className="py-3 px-5">
                      <div className="flex items-center gap-3">
                        <Avatar emp={e} />
                        <div><div className="font-semibold text-brand-navy">{e.first_name} {e.last_name}</div><div className="text-xs text-slate-500">{e.employee_number} · {e.job_title}</div></div>
                      </div>
                    </td>
                    <td className="py-3 px-3 hidden md:table-cell text-slate-600">{e.department}</td>
                    <td className="py-3 px-3 hidden lg:table-cell text-slate-600">{e.nature_of_employment}<span className="text-slate-400"> · {e.employment_type}</span></td>
                    <td className="py-3 px-3 hidden lg:table-cell text-slate-600">P{Number(e.wage_rate).toLocaleString()}<span className="text-slate-400 text-xs">/{e.pay_interval?.slice(0, 2).toLowerCase()}</span></td>
                    <td className="py-3 px-3"><Badge className={`${e.status === "Active" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-200 text-slate-600 hover:bg-slate-200"}`}>{e.status}</Badge></td>
                    <td className="py-3 px-5">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setDetail(e)} className="w-8 h-8 rounded-lg hover:bg-brand-light text-slate-500 hover:text-brand-blue flex items-center justify-center transition-colors" title="View"><Eye className="w-4 h-4" /></button>
                        <button onClick={() => { setEditing(e); setFormOpen(true); }} className="w-8 h-8 rounded-lg hover:bg-brand-light text-slate-500 hover:text-brand-blue flex items-center justify-center transition-colors" title="Edit"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => setToDelete(e)} className="w-8 h-8 rounded-lg hover:bg-red-50 text-slate-500 hover:text-destructive flex items-center justify-center transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <EmployeeForm open={formOpen} onOpenChange={setFormOpen} initial={editing} onSave={handleSave} />
      <EmployeeDetail emp={detail} open={!!detail} onOpenChange={(v) => !v && setDetail(null)} />

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove employee?</AlertDialogTitle>
            <AlertDialogDescription>This will remove {toDelete?.first_name} {toDelete?.last_name} from your records. This action is logged in the audit trail.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
